import type { Practitioner } from '@/mocks/practitioners';
import Stars from '@/pages/praticien/components/Stars';

interface PractitionerHeroProps {
  practitioner: Practitioner;
  onBook: () => void;
}

export default function PractitionerHero({ practitioner: p, onBook }: PractitionerHeroProps) {
  return (
    <section className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className={`shrink-0 w-20 h-20 md:w-24 md:h-24 flex items-center justify-center rounded-2xl ${p.color}`}>
          <span className="font-heading font-bold text-2xl md:text-3xl">{p.initials}</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 leading-tight">
              {p.name}
            </h1>
            {p.isAvailableNow && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-soft-pulse" />
                Disponible
              </span>
            )}
          </div>

          <p className="text-primary-700 font-medium mb-3">
            {p.type}
            {p.category ? ` · ${p.category}` : ''}
          </p>

          {(p.rating ?? 0) > 0 && (
            <a href="#avis" className="inline-flex items-center gap-2 mb-3 cursor-pointer">
              <Stars value={p.rating ?? 0} />
              <span className="text-sm text-foreground-700">
                <strong className="text-foreground-900">{(p.rating ?? 0).toFixed(1)}</strong>
                <span className="text-foreground-500"> · {p.reviews ?? 0} avis patients</span>
              </span>
            </a>
          )}

          <p className="text-sm text-foreground-600 flex items-center gap-1.5 mb-4">
            <i className="ri-map-pin-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
            {p.address}
            {p.district ? ` · ${p.district}, Rabat` : ''}
          </p>

          {p.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {p.tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium"
                >
                  <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center" />
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-background-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-foreground-800">
          <i className="ri-calendar-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
          Prochain créneau : <strong className="text-foreground-900">{p.nextSlot}</strong>
        </div>
        <button
          onClick={onBook}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center" />
          Prendre rendez-vous
        </button>
      </div>
    </section>
  );
}